import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, createVNode as _createVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, Transition as _Transition } from "vue"

const _hoisted_1 = { key: 2 }
const _hoisted_2 = ["colspan"]
const _hoisted_3 = {
  key: 0,
  class: "flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto sm:flex-row sm:gap-8 px-4 py-3 border-t border-gray-200"
}
const _hoisted_4 = { class: "flex-1 whitespace-nowrap text-normal text-gray-500" }
const _hoisted_5 = { class: "flex items-center space-x-2" }
const _hoisted_6 = { class: "whitespace-nowrap text-normal font-medium" }
const _hoisted_7 = { class: "flex items-center gap-x-4" }
const _hoisted_8 = { class: "flex items-center justify-center text-normal font-medium" }
const _hoisted_9 = { class: "flex items-center space-x-1" }
const _hoisted_10 = { class: "sr-only" }
const _hoisted_11 = { class: "sr-only" }
const _hoisted_12 = { class: "sr-only" }
const _hoisted_13 = { class: "sr-only" }
const _hoisted_14 = {
  key: 0,
  class: "absolute inset-0 z-30 flex items-center justify-center bg-white/80"
}
const _hoisted_15 = { class: "flex justify-center items-center gap-2" }
const _hoisted_16 = { class: "font-medium" }

import { ColumnDef, SortingState, PaginationState, FlexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useVueTable } from '@tanstack/vue-table';
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-vue-next';
import { computed, watch, ref, useSlots, onMounted, onBeforeUnmount } from 'vue';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Spinner from '@/components/ui/Spinner.vue';
import DataTableFacetFilter from './DataTableFacetFilter.vue';
import type { FacetOption, FacetSelection } from './types';


export default /*@__PURE__*/_defineComponent({
  __name: 'FacetedDataTable',
  props: {
    columns: {},
    data: {},
    loading: { type: Boolean, default: false },
    maxBodyHeight: { default: undefined },
    pageSize: {},
    pageSizeOptions: {},
    page: {},
    enablePagination: { type: Boolean, default: true },
    sorting: { default: undefined },
    columnFilters: { default: undefined },
    facets: { default: undefined },
    rowId: { type: Function, default: undefined },
    rowClass: { type: Function, default: undefined }
  },
  emits: ["update:sorting", "update:columnFilters", "update:pageSize", "update:page", "row-click"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const slots = useSlots();

const internalSorting = ref<SortingState>([]);
const internalColumnFilters = ref<FacetSelection>({});

const pagination = computed<PaginationState>({
    get: () => ({
        pageSize: props.pageSize,
        pageIndex: props.page - 1,
    }),
    set: ({ pageSize, pageIndex }: PaginationState) => {
        emit('update:pageSize', pageSize);
        emit('update:page', pageIndex + 1);
    },
});
const sorting = computed({
    get: () => props.sorting ?? internalSorting.value,
    set: (value) => {
        if (props.sorting !== undefined) {
            emit('update:sorting', value);
        } else {
            internalSorting.value = value;
        }
    },
});
const columnFilters = computed({
    get: () => props.columnFilters ?? internalColumnFilters.value,
    set: (value) => {
        if (props.columnFilters !== undefined) {
            emit('update:columnFilters', value);
        } else {
            internalColumnFilters.value = value;
        }
    },
});

const columns = computed(() =>
    props.columns.map((column) => ({
        ...column,
        enableSorting: column.enableSorting ?? false,
    }))
);

const table = useVueTable({
    initialState: {
        sorting: sorting.value,
        pagination: pagination.value,
    },
    get data() {
        return props.data;
    },
    get columns() {
        return columns.value;
    },
    state: {
        get sorting() {
            return sorting.value;
        },
        get pagination() {
            return pagination.value;
        },
    },
    onSortingChange: (updater) => {
        sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater;
    },
    onPaginationChange: (updater) => {
        pagination.value = typeof updater === 'function' ? updater(pagination.value) : updater;
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: props.rowId,
    autoResetPageIndex: false,
});

/**
 * Classes pinning a header cell to the right edge of the table's own horizontal scroll area (see
 * `Table.vue`'s `overflow-x-auto` wrapper), for a column whose meta declares `sticky: 'right'` - the
 * shared records table's actions column, today. Combined with `maxBodyHeight`'s top-sticky header,
 * the cell becomes a pinned corner: `z-[25]` sits deliberately above this row's own `z-20` (so the
 * corner stays above the plain header cells scrolling out from under it, and above the `z-10`
 * body cells below) but below the full-table loading overlay's `z-30`, which must still cover it.
 */
function headerStickyClass(sticky: 'right' | undefined): string {
    if (sticky === 'right' && props.maxBodyHeight) {
        return 'sticky top-0 right-0 z-[25] bg-white border-l border-gray-200';
    }

    if (sticky === 'right') {
        return 'sticky right-0 z-20 bg-white border-l border-gray-200';
    }

    return props.maxBodyHeight ? 'sticky top-0 z-20 bg-white' : '';
}

/**
 * Classes pinning a totals cell to the right edge, mirroring `headerStickyClass` above and replacing
 * its own previous plain `maxBodyHeight`-only stickiness so the two never disagree on `z-index`. The
 * totals row's background (`bg-gray-50`, applied unconditionally by its caller) already occludes
 * whatever scrolls underneath, so this only ever adds position and the border.
 */
function totalsStickyClass(sticky: 'right' | undefined): string {
    const isRightSticky = sticky === 'right';

    if (!props.maxBodyHeight && !isRightSticky) {
        return '';
    }

    if (props.maxBodyHeight && isRightSticky) {
        return 'sticky right-0 z-[25] border-l border-gray-200';
    }

    if (props.maxBodyHeight) {
        return 'sticky z-20';
    }

    return 'sticky right-0 z-20 border-l border-gray-200';
}

/**
 * Classes pinning a body cell to the right edge, mirroring the helpers above. A body row is never
 * vertically sticky, so this only ever adds the horizontal half - at `z-10`, below the header/totals
 * stack so a scrolled-past row's actions cell never floats above the sticky rows pinned over it, but
 * above plain body cells so it still occludes them as they scroll underneath. `group-hover:bg-gray-50`
 * mirrors `TableRow`'s own hover background, which this cell's opaque `bg-white` would otherwise hide.
 */
function bodyCellStickyClass(sticky: 'right' | undefined): string {
    if (sticky !== 'right') {
        return '';
    }

    return 'sticky right-0 z-10 bg-white group-hover:bg-gray-50 border-l border-gray-200';
}

function getAlignClass(align: string | undefined): string {
    switch (align) {
        case 'right':
            return 'text-right';
        case 'center':
            return 'text-center';
        default:
            return 'text-left';
    }
}

const rows = computed(() => table.getRowModel().rows || []);
const rowsCount = computed(() => props.data.length);
const visibleLeafColumns = computed(() => table.getVisibleLeafColumns());
/** A totals row is rendered when any column offers one, directly under the column names. */
const hasTotals = computed(() => visibleLeafColumns.value.some((column) => slots[`totals[${column.id}]`] !== undefined));

const start = computed(() => pagination.value.pageIndex * pagination.value.pageSize + 1);
const end = computed(() => {
    const itemEnd = (pagination.value.pageIndex + 1) * pagination.value.pageSize;

    return rowsCount.value > itemEnd ? itemEnd : rowsCount.value;
});

function facetFor(columnId: string) {
    return props.facets?.[columnId];
}

function facetValue(columnId: string): (string | number)[] {
    return columnFilters.value[columnId] ?? [];
}

function setFacetValue(columnId: string, value: (string | number)[]) {
    const next = { ...columnFilters.value };

    if (value.length) {
        next[columnId] = value;
    } else {
        delete next[columnId];
    }

    columnFilters.value = next;
}

/*
 * Height of the header row, which the sticky totals row underneath pins itself to. Measured because
 * a header cell is `h-12` at its shortest but grows whenever a translated heading wraps, and a
 * fixed offset would then let the totals overlap the names they sit under.
 */
const root = ref<HTMLElement | null>(null);
const headerHeight = ref(0);
let headerResize: ResizeObserver | null = null;

onMounted(() => {
    const head = root.value?.querySelector('thead');

    if (!head) {
        return;
    }

    headerHeight.value = head.getBoundingClientRect().height;
    headerResize = new ResizeObserver(() => {
        headerHeight.value = head.getBoundingClientRect().height;
    });
    headerResize.observe(head);
});

onBeforeUnmount(() => {
    headerResize?.disconnect();
    headerResize = null;
});

watch([() => rows.value, () => columnFilters.value], () => {
    if (props.loading) {
        return;
    }

    const pageCount = table.getPageCount();

    // Filtering can shrink the set below the page the user is standing on.
    if (pagination.value.pageIndex > pageCount - 1) {
        table.setPageIndex(0);
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "relative"
  }, [
    _createVNode(_unref(Table), {
      "container-class": _ctx.maxBodyHeight ? ['overflow-y-auto', _ctx.maxBodyHeight] : undefined
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(TableHeader), null, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getHeaderGroups(), (headerGroup) => {
              return (_openBlock(), _createBlock(_unref(TableRow), {
                key: headerGroup.id,
                class: "hover:bg-inherit"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerGroup.headers, (header) => {
                    return (_openBlock(), _createBlock(_unref(TableHead), {
                      key: header.id,
                      class: _normalizeClass([getAlignClass(header.column.columnDef.meta?.align), header.column.columnDef.meta?.headerClass || '', headerStickyClass(header.column.columnDef.meta?.sticky)])
                    }, {
                      default: _withCtx(() => [
                        _renderSlot(_ctx.$slots, `header[${header.column.id}]`, {
                          column: header.column
                        }, () => [
                          _createElementVNode("div", {
                            class: _normalizeClass(["flex items-center gap-x-0.5", { 'justify-end': header.column.columnDef.meta?.align === 'right', 'justify-center': header.column.columnDef.meta?.align === 'center' }])
                          }, [
                            (header.column.getCanSort() && !header.isPlaceholder)
                              ? (_openBlock(), _createBlock(_unref(DropdownMenu), {
                                  key: 0,
                                  modal: false
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(Button), {
                                          "aria-label": 
                                                header.column.getIsSorted()
                                                    ? header.column.getIsSorted() === 'asc'
                                                        ? _ctx.$t('global.uiElements.dataTable.buttons.sortedAscendingClickToSort')
                                                        : _ctx.$t('global.uiElements.dataTable.buttons.sortedDescendingClickToSort')
                                                    : _ctx.$t('global.uiElements.dataTable.buttons.clickToSort')
                                            ,
                                          variant: "ghost",
                                          class: "h-auto px-3 -ml-3 gap-x-1.5 data-[state=open]:bg-gray-100"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(FlexRender), {
                                              render: header.column.columnDef.header,
                                              props: header.getContext()
                                            }, null, 8, ["render", "props"]),
                                            _createVNode(FadeTransition, { mode: "out-in" }, {
                                              default: _withCtx(() => [
                                                (header.column.getIsSorted() === 'asc')
                                                  ? (_openBlock(), _createBlock(_unref(ArrowUp), {
                                                      key: 0,
                                                      class: "h-4 w-4 -mr-0.5",
                                                      "aria-hidden": "true"
                                                    }))
                                                  : (header.column.getIsSorted())
                                                    ? (_openBlock(), _createBlock(_unref(ArrowDown), {
                                                        key: 1,
                                                        class: "h-4 w-4 -mr-0.5",
                                                        "aria-hidden": "true"
                                                      }))
                                                    : (_openBlock(), _createBlock(_unref(ChevronsUpDown), {
                                                        key: 2,
                                                        class: "h-4 w-4 transition-transform -mr-0.5",
                                                        "aria-hidden": "true"
                                                      }))
                                              ]),
                                              _: 2
                                            }, 1024)
                                          ]),
                                          _: 2
                                        }, 1032, ["aria-label"])
                                      ]),
                                      _: 2
                                    }, 1024),
                                    _createVNode(_unref(DropdownMenuContent), {
                                      align: "start",
                                      class: "space-y-0.5"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(DropdownMenuItem), {
                                          "aria-label": _ctx.$t('global.uiElements.dataTable.buttons.sortAscending'),
                                          class: _normalizeClass({ 'bg-gray-100': header.column.getIsSorted() === 'asc' }),
                                          onSelect: ($event: any) => (header.column.toggleSorting(false))
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(ArrowUp), { class: "mr-2 h-4 w-4" }),
                                            _createElementVNode("span", null, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.ascending')), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["aria-label", "class", "onSelect"]),
                                        _createVNode(_unref(DropdownMenuItem), {
                                          "aria-label": _ctx.$t('global.uiElements.dataTable.buttons.sortDescending'),
                                          class: _normalizeClass({ 'bg-gray-100': header.column.getIsSorted() === 'desc' }),
                                          onSelect: ($event: any) => (header.column.toggleSorting(true))
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(ArrowDown), { class: "mr-2 h-4 w-4" }),
                                            _createElementVNode("span", null, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.descending')), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["aria-label", "class", "onSelect"])
                                      ]),
                                      _: 2
                                    }, 1024)
                                  ]),
                                  _: 2
                                }, 1024))
                              : (!header.isPlaceholder)
                                ? (_openBlock(), _createBlock(_unref(FlexRender), {
                                    key: 1,
                                    render: header.column.columnDef.header,
                                    props: header.getContext()
                                  }, null, 8, ["render", "props"]))
                                : _createCommentVNode("", true),
                            (facetFor(header.column.id))
                              ? (_openBlock(), _createBlock(DataTableFacetFilter, {
                                  key: 2,
                                  title: facetFor(header.column.id)?.title ?? '',
                                  options: facetFor(header.column.id)?.options ?? [],
                                  "select-all": facetFor(header.column.id)?.selectAll,
                                  "model-value": facetValue(header.column.id),
                                  "onUpdate:modelValue": ($event: any) => (setFacetValue(header.column.id, $event))
                                }, null, 8, ["title", "options", "select-all", "model-value", "onUpdate:modelValue"]))
                              : _createCommentVNode("", true)
                          ], 2)
                        ])
                      ]),
                      _: 2
                    }, 1032, ["class"]))
                  }), 128))
                ]),
                _: 2
              }, 1024))
            }), 128))
          ]),
          _: 3
        }),
        _createVNode(_unref(TableBody), { class: "relative" }, {
          default: _withCtx(() => [
            (hasTotals.value)
              ? (_openBlock(), _createBlock(_unref(TableRow), {
                  key: 0,
                  class: "hover:bg-inherit"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleLeafColumns.value, (column) => {
                      return (_openBlock(), _createBlock(_unref(TableCell), {
                        key: column.id,
                        class: _normalizeClass([getAlignClass(column.columnDef.meta?.align), 'bg-gray-50 font-semibold text-gray-900', totalsStickyClass(column.columnDef.meta?.sticky)]),
                        style: _normalizeStyle(_ctx.maxBodyHeight ? { top: `${headerHeight.value}px` } : undefined)
                      }, {
                        default: _withCtx(() => [
                          _renderSlot(_ctx.$slots, `totals[${column.id}]`)
                        ]),
                        _: 2
                      }, 1032, ["class", "style"]))
                    }), 128))
                  ]),
                  _: 3
                }))
              : _createCommentVNode("", true),
            (rows.value.length)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(rows.value, (row) => {
                  return (_openBlock(), _createBlock(_unref(TableRow), {
                    key: row.id,
                    class: _normalizeClass(_ctx.rowClass?.(row.original as TData)),
                    onClick: ($event: any) => (emit('row-click', row.original as TData))
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.getVisibleCells(), (cell) => {
                        return (_openBlock(), _createBlock(_unref(TableCell), {
                          key: cell.id,
                          class: _normalizeClass([getAlignClass(cell.column.columnDef.meta?.align), cell.column.columnDef.meta?.cellClass || '', bodyCellStickyClass(cell.column.columnDef.meta?.sticky)])
                        }, {
                          default: _withCtx(() => [
                            _renderSlot(_ctx.$slots, `item[${cell.column.id}]`, {
                              item: row.original,
                              value: cell.getValue()
                            }, () => [
                              _createVNode(_unref(FlexRender), {
                                render: cell.column.columnDef.cell,
                                props: cell.getContext()
                              }, null, 8, ["render", "props"])
                            ])
                          ]),
                          _: 2
                        }, 1032, ["class"]))
                      }), 128))
                    ]),
                    _: 2
                  }, 1032, ["class", "onClick"]))
                }), 128))
              : (_openBlock(), _createElementBlock("tr", _hoisted_1, [
                  _createElementVNode("td", {
                    colspan: visibleLeafColumns.value.length,
                    class: "h-24 text-center text-gray-500"
                  }, _toDisplayString(_ctx.loading ? '' : _ctx.$t('global.uiElements.dataTable.texts.noResults')), 9, _hoisted_2)
                ]))
          ]),
          _: 3
        })
      ]),
      _: 3
    }, 8, ["container-class"]),
    (_ctx.enablePagination)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('global.uiElements.dataTable.texts.showingRange')
                        .replace('%START%', rows.value.length ? start.value.toString() : '0')
                        .replace('%END%', end.value.toString())
                        .replace('%TOTAL%', rowsCount.value.toString())), 1),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t('global.uiElements.dataTable.texts.pageSize')), 1),
            _createVNode(_unref(Select), {
              "model-value": String(pagination.value.pageSize),
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_unref(table).setPageSize(Number($event))))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(SelectTrigger), { class: "w-24" }, {
                  icon: _withCtx(() => [
                    _createVNode(_unref(ChevronsUpDown), {
                      class: "size-4 opacity-50 flex-shrink-0",
                      "aria-hidden": "true"
                    })
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_unref(SelectValue), {
                      placeholder: String(pagination.value.pageSize)
                    }, null, 8, ["placeholder"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(SelectContent), { side: "top" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.pageSizeOptions, (option) => {
                      return (_openBlock(), _createBlock(_unref(SelectItem), {
                        key: option,
                        value: String(option),
                        class: "text-normal"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(option), 1)
                        ]),
                        _: 2
                      }, 1032, ["value"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["model-value"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, _toDisplayString(_ctx.$t('global.uiElements.dataTable.texts.page')
                            .replace('%CURRENT_PAGE%', (pagination.value.pageIndex + 1).toString())
                            .replace('%TOTAL%', (_unref(table).getPageCount() || 1).toString())), 1),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(_unref(TooltipProvider), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(Tooltip), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Button), {
                            variant: "outline",
                            size: "icon",
                            disabled: !_unref(table).getCanPreviousPage() || _ctx.loading,
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(table).setPageIndex(0)))
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.firstPage')), 1),
                              _createVNode(_unref(ChevronsLeft), {
                                class: "size-5",
                                "aria-hidden": "true"
                              })
                            ]),
                            _: 1
                          }, 8, ["disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(TooltipContent), null, {
                        default: _withCtx(() => [
                          _createElementVNode("p", null, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.firstPage')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_unref(TooltipProvider), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(Tooltip), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Button), {
                            variant: "outline",
                            size: "icon",
                            disabled: !_unref(table).getCanPreviousPage() || _ctx.loading,
                            onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(table).previousPage()))
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.previousPage')), 1),
                              _createVNode(_unref(ChevronLeft), {
                                class: "size-5",
                                "aria-hidden": "true"
                              })
                            ]),
                            _: 1
                          }, 8, ["disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(TooltipContent), null, {
                        default: _withCtx(() => [
                          _createElementVNode("p", null, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.previousPage')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_unref(TooltipProvider), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(Tooltip), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Button), {
                            variant: "outline",
                            size: "icon",
                            disabled: !_unref(table).getCanNextPage() || _ctx.loading,
                            onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(table).nextPage()))
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.nextPage')), 1),
                              _createVNode(_unref(ChevronRight), {
                                class: "size-5",
                                "aria-hidden": "true"
                              })
                            ]),
                            _: 1
                          }, 8, ["disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(TooltipContent), null, {
                        default: _withCtx(() => [
                          _createElementVNode("p", null, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.nextPage')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_unref(TooltipProvider), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(Tooltip), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Button), {
                            variant: "outline",
                            size: "icon",
                            disabled: !_unref(table).getCanNextPage() || _ctx.loading,
                            onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(table).setPageIndex(_unref(table).getPageCount() - 1)))
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_13, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.lastPage')), 1),
                              _createVNode(_unref(ChevronsRight), {
                                class: "size-5",
                                "aria-hidden": "true"
                              })
                            ]),
                            _: 1
                          }, 8, ["disabled"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(TooltipContent), null, {
                        default: _withCtx(() => [
                          _createElementVNode("p", null, _toDisplayString(_ctx.$t('global.uiElements.dataTable.buttons.lastPage')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_Transition, {
      "enter-active-class": "duration-200 ease-out",
      "enter-from-class": "opacity-0",
      "enter-to-class": "opacity-100",
      "leave-active-class": "duration-100 ease-in",
      "leave-from-class": "opacity-100",
      "leave-to-class": "opacity-0"
    }, {
      default: _withCtx(() => [
        (_ctx.loading)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, _toDisplayString(_ctx.$t('global.uiElements.dataTable.texts.loading')), 1),
                _createVNode(Spinner, { class: "w-5 h-5 animate-spin" })
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 512))
}
}

})